import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalFooter } from '../../components/Shared/Modal.tsx';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../components/Shared/Table.tsx';
import { formatDate } from '../../utils/dateUtils.ts';
import { formatHours, formatSignedHours } from '../TimeClock/components/overtimeFormat.ts';
import { groupDeviating } from './deviatingGroups.ts';
import type { FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsWorkScheduleModelChangePreviewDto as ChangePreviewDto } from '../../api/generated/models';

interface WorkScheduleModelChangePreviewModalProps {
  preview: ChangePreviewDto | null;
  onCancel: () => void;
  /** Called with the employees whose deviating days get the model's plan. */
  onConfirm: (overwriteDeviationsFor: string[]) => void;
}

/**
 * Shows what a work schedule model change from a past date does to the planning before it is
 * saved: adjusted, created, removed and skipped work days per employee, the deviating days (which
 * follow neither the old nor the new plan, e.g. from a wrong model assigned before) with a tick
 * per employee, and the effect on the overtime balance (FAFD-67).
 */
export function WorkScheduleModelChangePreviewModal({
  preview,
  onCancel,
  onConfirm,
}: WorkScheduleModelChangePreviewModalProps) {
  const { t } = useTranslation(['workScheduleModels', 'common']);
  const employees = preview?.employees ?? [];
  const [overwrite, setOverwrite] = useState<Set<string>>(new Set());

  // Every employee with deviating days starts ticked — the usual case is a wrong model.
  useEffect(() => {
    setOverwrite(
      new Set(
        (preview?.employees ?? [])
          .filter((e) => (e.deviatingDays?.length ?? 0) > 0)
          .map((e) => e.employeeId!)
      )
    );
  }, [preview]);

  const toggle = (employeeId: string) =>
    setOverwrite((current) => {
      const next = new Set(current);
      if (next.has(employeeId)) next.delete(employeeId);
      else next.add(employeeId);
      return next;
    });

  const effectOf = (employee: (typeof employees)[number]) =>
    (employee.overtimeEffectHours ?? 0) +
    (overwrite.has(employee.employeeId!) ? employee.deviationOvertimeEffectHours ?? 0 : 0);
  const effect = Math.round(employees.reduce((sum, e) => sum + effectOf(e), 0) * 100) / 100;

  return (
    <Modal
      isOpen={preview !== null}
      onClose={onCancel}
      title={t('workScheduleModels:preview.title')}
      size="xl"
      footer={
        <ModalFooter
          onCancel={onCancel}
          onConfirm={() => onConfirm([...overwrite])}
          confirmText={t('workScheduleModels:preview.confirm')}
          cancelText={t('common:actions.cancel')}
        />
      }
    >
      {preview && (
        <div className="space-y-4">
          <p>{t('workScheduleModels:preview.intro', { date: formatDate(preview.validFrom) })}</p>

          <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
            <Figure label={t('workScheduleModels:preview.updated')} value={String(preview.updatedDays ?? 0)} />
            <Figure label={t('workScheduleModels:preview.deviating')} value={String(preview.deviatingDays ?? 0)} />
            <Figure label={t('workScheduleModels:preview.created')} value={String(preview.createdDays ?? 0)} />
            <Figure label={t('workScheduleModels:preview.removed')} value={String(preview.removedDays ?? 0)} />
            <Figure label={t('workScheduleModels:preview.skipped')} value={String(preview.skippedDays ?? 0)} />
            <Figure label={t('workScheduleModels:preview.overtimeEffect')} value={formatSignedHours(effect)} />
          </div>

          {effect !== 0 && (
            <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              {t(effect > 0 ? 'workScheduleModels:preview.overtimeUp' : 'workScheduleModels:preview.overtimeDown', {
                hours: formatHours(Math.abs(effect)),
              })}
            </p>
          )}

          {(preview.deviatingDays ?? 0) > 0 && (
            <p className="text-[12px] text-slate-500">{t('workScheduleModels:preview.deviatingHint')}</p>
          )}
          {(preview.skippedDays ?? 0) > 0 && (
            <p className="text-[12px] text-slate-500">{t('workScheduleModels:preview.skippedHint')}</p>
          )}

          {employees.length > 0 && (
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>{t('workScheduleModels:preview.employee')}</TableHeader>
                  <TableHeader>{t('workScheduleModels:preview.updated')}</TableHeader>
                  <TableHeader>{t('workScheduleModels:preview.deviating')}</TableHeader>
                  <TableHeader className="hidden sm:table-cell">{t('workScheduleModels:preview.created')}</TableHeader>
                  <TableHeader className="hidden sm:table-cell">{t('workScheduleModels:preview.removed')}</TableHeader>
                  <TableHeader className="hidden md:table-cell">{t('workScheduleModels:preview.skipped')}</TableHeader>
                  <TableHeader>{t('workScheduleModels:preview.overtimeEffect')}</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {employees.map((employee) => {
                  const deviating = employee.deviatingDays ?? [];
                  return (
                    <TableRow key={employee.employeeId}>
                      <TableCell>
                        <span className="font-medium text-slate-900">{employee.name}</span>
                      </TableCell>
                      <TableCell>
                        <span className="tabular-nums">{employee.updatedDays}</span>
                      </TableCell>
                      <TableCell>
                        {deviating.length === 0 ? (
                          <span className="tabular-nums">0</span>
                        ) : (
                          <div className="space-y-1">
                            <label className="flex items-center gap-2 text-[12px] text-slate-700">
                              <input
                                type="checkbox"
                                checked={overwrite.has(employee.employeeId!)}
                                onChange={() => toggle(employee.employeeId!)}
                                className="w-4 h-4 rounded border-slate-300 text-[#00A871] focus:ring-2 focus:ring-[#00A871]/30"
                              />
                              {t('workScheduleModels:preview.overwriteDeviating', { count: deviating.length })}
                            </label>
                            {groupDeviating(deviating).map((group) => (
                              <div
                                key={`${group.current}-${group.model}`}
                                className="text-[11px] text-slate-500 tabular-nums whitespace-nowrap"
                                title={group.dates.map((date) => formatDate(date)).join(', ')}
                              >
                                {t('workScheduleModels:preview.deviatingGroup', {
                                  count: group.dates.length,
                                  current: formatHours(group.current),
                                  model: formatHours(group.model),
                                  from: formatDate(group.dates[0]),
                                  to: formatDate(group.dates[group.dates.length - 1]),
                                })}
                              </div>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <span className="tabular-nums">{employee.createdDays}</span>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <span className="tabular-nums">{employee.removedDays}</span>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {(employee.skippedDates?.length ?? 0) === 0 ? (
                          <span className="tabular-nums">0</span>
                        ) : (
                          <span className="tabular-nums text-[12px] text-slate-600">
                            {employee.skippedDates!.map((date) => formatDate(date)).join(', ')}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="tabular-nums">{formatSignedHours(effectOf(employee))}</span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      )}
    </Modal>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-slate-200 px-3 py-2">
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="text-[15px] font-semibold text-slate-900 tabular-nums font-mono">{value}</div>
    </div>
  );
}
